import {
  StateToText,
  ClaimTypeToText,
  formatPrice,
  formatDate,
  humanizeCount,
  ResolutionTypeToText,
  formatDate2,
} from '../../../helpers';

import { REFUND, RESHIP, CUSTOM, STORE_CREDIT, FLAT_RATE } from '@pulse/constants/claims';

import { BaseModel } from './base.model';
import { ClaimItemModel } from './claim-item.model';
import { ClaimResolutionModel } from './claim-resolution.model';
import countBy from 'lodash/countBy';
import { Claims } from '@database/types';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
dayjs.extend(duration);

export class ClaimModel extends BaseModel<any> {
  get id() {
    return this._data.id;
  }

  get documents() {
    return this._data.claimDocuments;
  }

  get uploadedPhotos() {
    return this._data.claimItems.flatMap((item: any) => item.images);
  }

  get productImages() {
    return this._data.claimItems.flatMap((item: any) => item?.imageUrl).filter(Boolean);
  }

  get warrantyValue() {
    const items = this._data.claimItems ?? [];
    const values = items.map((item: any) => item?.claimItemMetadata?.productValue);
    // If any item's price is unknown (null), surface 'N/A' instead of folding nulls into $0 —
    // matches the warranty CSV export and preserves the nullable column's distinction.
    if (items.length && values.some((v: number | null | undefined) => v == null)) return 'N/A';
    const total = values.reduce((sum: number, v: number | null | undefined) => sum + (v ?? 0), 0);
    return formatPrice(total, this._data.policy?.premiumCurrency || 'USD');
  }

  get warrantyFee() {
    const total = (this._data.claimItems ?? []).reduce(
      (sum: number, item: any) => sum + (item?.claimItemMetadata?.warrantyFee ?? 0),
      0,
    );
    return formatPrice(total, 'USD');
  }

  get warrantyValidUntil() {
    const dateOfPurchase = this._data.offlineMetadata?.dateOfPurchase;
    const warrantyPeriod = this._data.claimItems?.[0]?.claimItemMetadata?.warrantyPeriod;

    try {
      const parsedDuration = dayjs.duration(warrantyPeriod);
      const endDate = dayjs(dateOfPurchase).add(parsedDuration);

      return endDate.format('MMM D, YYYY');
    } catch (error) {
      return 'Invalid warranty period';
    }
  }

  get claimCategory() {
    return this._data.claimCategory as Claims.ClaimCategory;
  }

  get isPastTimeframe() {
    return (
      this._data.claimCategory === Claims.ClaimCategory.SHIPPING &&
      !!this._data.closedAt &&
      dayjs().diff(dayjs(this._data.closedAt), 'day') > 30
    );
  }

  get claimSubtype() {
    return this._data.claimSubtype;
  }

  get userId() {
    return this._data.userId;
  }

  get printableId() {
    return this._data.sourceOrderNumber;
  }

  get order() {
    return this._data.order;
  }

  get coverageRules() {
    return this._data.coverageRules;
  }

  get includeOtasInReimbursements(): boolean {
    return !!this._data.includeOtasInReimbursements;
  }

  get policy() {
    return this._data.policy;
  }

  get sourceOrderId() {
    return this._data.sourceOrderId;
  }

  get sourceOrderNumber() {
    return this._data.sourceOrderNumber;
  }

  get orderId() {
    return this._data.orderId;
  }

  get claimDescription() {
    return this._data.claimDescription;
  }

  get stateText() {
    return StateToText[this._data.claimState] || 'Unknown';
  }

  get state() {
    return this._data.claimState?.toLowerCase();
  }

  get rawState() {
    return this._data.claimState;
  }

  get isResolvable() {
    return this.resolutions.filter(r => !r.deletedAt).length === 0;
  }

  get hasApprovedResolution() {
    return this.resolutions.some((r: ClaimResolutionModel) =>
      [REFUND, RESHIP, STORE_CREDIT, FLAT_RATE, CUSTOM].includes(r.providedResolution),
    );
  }

  get salesChannel() {
    return this._data.salesChannel;
  }

  get offlineMetadata() {
    return this._data.offlineMetadata;
  }

  get warrantyDuration() {
    return this._data.claimItems?.[0]?.claimItemMetadata?.warrantyPeriod;
  }

  get warrantyProductDescription() {
    return this._data.claimItems?.[0]?.claimItemMetadata?.productDescription;
  }

  get warrantyDateOfPurchase() {
    return this._data.offlineMetadata?.dateOfPurchase;
  }

  get stateBadge() {
    switch (this.state) {
      case 'new':
        return 'info';
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'denied':
        return 'danger';
      case 'dismissed':
        return 'neutral';
      case 'closed':
        return 'neutral';
      case 'draft':
        return 'warning';
      default:
        return 'info';
    }
  }

  get protectionStatus() {
    if (this.isOta) return 'ota';

    if (this._data.policy?.removedAt) return 'removed';

    return this._data.policy !== null ? 'protected' : 'unprotected';
  }

  get isOta() {
    return this._data.isOta;
  }

  get author() {
    return this._data.authorName;
  }

  get customer() {
    return this.author;
  }

  get authorEmail() {
    return this._data.authorEmail;
  }

  get store() {
    return this._data.storeId || "Leo's";
  }

  get storeId(): string | undefined {
    return this._data.storeId;
  }

  get assignedToUserId(): string | null | undefined {
    return this._data.assignedToUserId;
  }

  get total() {
    return formatPrice(this._data.policy?.insuredAmount || 0, this._data.policy?.premiumCurrency || 'USD');
  }

  get description() {
    return this._data.claimDescription;
  }

  get protectionCost() {
    return formatPrice(this._data.policy?.premiumPaid || 0, this._data.policy?.premiumCurrency || 'USD');
  }

  get date() {
    return formatDate(this._data.createdAt);
  }

  get createdAt() {
    return this._data.createdAt;
  }

  get dateV2() {
    return formatDate2(this._data.createdAt);
  }

  get updatedAt() {
    return formatDate(this._data.updatedAt);
  }

  get type() {
    return 'shipping_claim';
  }

  get storeCredits() {
    return this._data.claimStoreCredits;
  }

  set storeCredits(storeCredit) {
    this._data.claimStoreCredits = storeCredit;
  }

  get resolutionType() {
    return this.resolutions[0].type;
  }

  get resolutionTypeForCustomer() {
    return this.resolutions[0].typeForCustomer;
  }

  get claimType() {
    return ClaimTypeToText[this._data.claimType];
  }

  get rawClaimType() {
    return this._data.claimType;
  }

  set rawClaimType(claimType) {
    this._data.claimType = claimType;
  }

  get authorName() {
    return this._data.authorName;
  }

  get denialReason() {
    return ResolutionTypeToText[this.resolution?.providedResolution];
  }

  get claimStateBadge() {
    const typeMap = {
      approved: 'success',
      closed: 'neutral',
      denied: 'danger',
      dismissed: 'neutral',
      fulfilled: 'neutral',
      open: 'primary',
      payment: 'neutral',
      unfulfilled: 'neutral',
    } as any;

    return typeMap[this.state.toLowerCase()] || 'neutral';
  }

  get claimTypeBadge() {
    return 'danger';
  }

  get url() {
    return `/claims/view/${this.orderId}`;
  }

  get resolutions() {
    return ClaimResolutionModel.fromArrayExtending(this._data.claimResolutionRecords, {
      claim: this,
    }) as ClaimResolutionModel[];
  }

  get claimResolutionLines() {
    return this.resolution?.lines || [];
  }

  get hasResolutionLines() {
    return this.claimResolutionLines.length > 0;
  }

  get activeAutomation() {
    if (this._data.activeAutomation !== undefined) return this._data.activeAutomation;
    return this._data.claimAutomations.find(a => a.isActive);
  }

  set activeAutomation(a) {
    this._data.activeAutomation = a;
  }

  get automationHistory() {
    return this._data.claimAutomations;
  }

  // Presence of a pause row means support put the claim in manual mode.
  get automationPause() {
    return this._data.automationPause;
  }

  get isAutomationPaused() {
    return !!this._data.automationPause;
  }

  // The watchdog has already nudged whoever took this over and nothing has happened since.
  get isAutomationPauseOverdue() {
    return !!this._data.automationPause?.nudgedAt;
  }

  // Set by the claims list: an escalation on the claim still waits on our direction, whatever the AI has done since.
  get hasOpenEscalation() {
    return !!this._data.escalationOpen;
  }

  get hasClaimReset() {
    return (this._data.claimReset?.length ?? 0) > 0;
  }

  get resolution() {
    return this.resolutions.find(r => !r.deletedAt);
  }

  get resolutionRequested() {
    const resolutions = countBy(this.claimItems, 'resolutionRequested');

    if (Object.keys(resolutions).length > 1) {
      return CUSTOM;
    } else if (resolutions[REFUND] > 0) {
      return REFUND;
    } else if (resolutions[RESHIP] > 0) {
      return RESHIP;
    } else if (resolutions[STORE_CREDIT] > 0) {
      return STORE_CREDIT;
    }

    return null;
  }

  get providedResolution() {
    return this.resolutions[0]?.providedResolution;
  }

  get creditStore() {
    return this.resolutions.find((r: any) => r.isCreditStore);
  }

  get refundShipping() {
    return this.resolutions.find((r: any) => r.isRefundShipping);
  }

  get replacementOrder() {
    return this.resolutions.find((r: any) => r.isReplacementOrder);
  }

  get details() {
    return {
      lineItems: [],
    };
  }

  get subtotal() {
    return this.claimItems
      .reduce((total, item) => total + (item.price - item.discount) * item.validQuantity * this.exchangeRate, 0)
      .toFixed(2);
  }

  get currency() {
    return this.order.currency;
  }

  get shippingCost() {
    return this.order.shippingCost;
  }
  get shippingCostTax() {
    return this.order.shippingCostTax;
  }

  get totalValue() {
    const lineItemsSum = this.claimItems.reduce((total, item) => {
      return total + item.price * item.quantity; // Multiply the amount by the quantity
    }, 0);

    const taxesAmount = parseFloat(this.order.tax);
    const shippingAmount = parseFloat(this.order.shippingCost);
    const totalAmount = lineItemsSum + taxesAmount + shippingAmount;
    const totalAmountInUsd = totalAmount * this.exchangeRate;

    return totalAmountInUsd.toFixed(2);
  }

  get claimItems() {
    return ClaimItemModel.fromArrayExtending(this._data.claimItems, {
      claim: this,
    }) as ClaimItemModel[];
  }

  get messages() {
    return this._data.claimMessages;
  }

  get itemsCount() {
    return this._data.claimItems?.length || this._data._count?.claimItems || 0;
  }

  get exchangeRate() {
    return this.order.exchangeRate;
  }

  get itemsCountHumanized() {
    return humanizeCount(this.itemsCount, 'item', 'items');
  }

  get policyId() {
    return this._data.policy?.id;
  }

  get policyType() {
    return this._data.policy?.policyType;
  }

  get pricingModel() {
    return this._data.policy?.pricingModel;
  }

  /**
   * What the platform services reported doing with what this claim promised. Empty until a claim is
   * resolved, and on platforms with no delivering service at all.
   */
  get deliveryOutcomes() {
    return (this._data.resolutionOutcomes ?? []).filter((outcome: any) => !outcome.deletedAt);
  }

  /**
   * Only the deliveries that did not land. A delivery that worked needs no telling; MISMATCH landed
   * and is its own thing; INDETERMINATE is deliberately not here — the check could not tell, which
   * is not an accusation.
   */
  get deliveryFailures() {
    return this.deliveryOutcomes.filter((outcome: any) => outcome.status === Claims.ResolutionStatus.FAILED);
  }

  get hasDeliveryFailure() {
    return this.deliveryFailures.length > 0;
  }

  /** Deliveries that landed for the wrong amount — owed the difference, not a redelivery. */
  get deliveryMismatches() {
    return this.deliveryOutcomes.filter((outcome: any) => outcome.status === Claims.ResolutionStatus.MISMATCH);
  }

  /**
   * Anything other than delivered or still going: a broken promise, or one we could not confirm.
   * Wider than `hasDeliveryFailure` on purpose — a list icon cannot express the difference, so it
   * flags everything worth opening the claim for, and the claim itself says which it is.
   */
  get deliveryNeedsAttention() {
    const settled: string[] = [Claims.ResolutionStatus.SUCCESS, Claims.ResolutionStatus.PENDING];

    return this.deliveryOutcomes.some((outcome: any) => !settled.includes(outcome.status));
  }

  /**
   * Shown whenever a delivery wants attention, worded for which kind it is. Saying "did not go
   * through" about an amount mismatch or an unconfirmed delivery would be a claim the check never
   * made, so the worst case present picks the wording.
   */
  get deliveryAttentionMessage(): string | null {
    if (!this.deliveryNeedsAttention) return null;
    if (this.hasDeliveryFailure) return 'This claim was resolved but the resolution did not go through.';
    if (this.deliveryMismatches.length > 0) {
      return 'This claim was resolved but a different amount was delivered than was promised.';
    }

    return 'This claim was resolved but the resolution could not be confirmed.';
  }

  set messages(messages) {
    this._data.claimMessages = messages;
  }
}
